import React from "react";
import { Star } from "lucide-react";
import { useFavoriteStores } from "@/hooks/useFavoriteStores";

/**
 * Star toggle that marks a dispensary as the user's favorite store.
 * Sign-in required. Persists server-side (`consumer_favorite_stores`).
 *
 * Props:
 *   slug   — dispensary slug (required)
 *   name   — display name used in toast feedback
 *   size   — "sm" (default) or "lg"
 *   compact — render an inline pill instead of a circular icon button
 */
export const FavoriteStoreButton = ({
  slug,
  name = "",
  size = "sm",
  compact = false,
}) => {
  const { isFav, toggle } = useFavoriteStores();
  const active = isFav(slug);
  const big = size === "lg";

  if (compact) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle(slug, name);
        }}
        data-testid={`favorite-store-pill-${slug}`}
        aria-pressed={active}
        className={`inline-flex items-center gap-1.5 rounded-full text-xs h-7 px-2.5 transition-colors ${
          active
            ? "bg-amber-400/20 text-amber-200 border border-amber-400/50"
            : "bg-background/70 text-foreground/70 border border-border hover:text-amber-200 hover:border-amber-400/40"
        }`}
      >
        <Star
          className="w-3 h-3"
          strokeWidth={2}
          fill={active ? "currentColor" : "none"}
        />
        {active ? "Favorite" : "Save"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(slug, name);
      }}
      data-testid={`favorite-store-btn-${slug}`}
      aria-label={active ? "Remove favorite store" : "Save as favorite store"}
      aria-pressed={active}
      className={`group inline-flex items-center justify-center rounded-full backdrop-blur transition-all ${
        big ? "h-10 w-10" : "h-8 w-8"
      } ${
        active
          ? "bg-amber-400/90 text-foreground shadow-[0_0_20px_rgba(245,179,68,0.5)]"
          : "bg-background/70 text-foreground/70 border border-border hover:text-amber-200 hover:border-amber-400/40"
      }`}
    >
      <Star
        className={big ? "w-4 h-4" : "w-3.5 h-3.5"}
        strokeWidth={2}
        fill={active ? "currentColor" : "none"}
      />
    </button>
  );
};
